import { TimeIcon } from "@vericus/cadmus-icons";
import { colors, Pill, Text, Tooltip } from "@vericus/cadmus-ui";

import { formatDistanceStrict } from "date-fns";

interface ExtensionPillProps {
  originalDueDate: string;
  extendedDueDate: string;
  /**
   * Renders the "Extension applied" treatment, for use where the pill has
   * to explain itself without a surrounding due-date sentence. Defaults to
   * the compact "Extension" tag used inline elsewhere.
   */
  alert?: boolean;
}

/**
 * Recast `formatDistanceStrict`'s "3 days" into the adjectival "a 3-day", so
 * the tooltip reads "a 3-day extension" rather than "a 3 days extension".
 * The article tracks how the numeral is *said*: eight, eleven, eighteen and
 * the eighties all open on a vowel and take "an".
 */
export function asExtensionLength(distance: string) {
  return {
    article: /^(8|11|18|8\d)\b/.test(distance) ? "an" : "a",
    length: distance.replace(/^(\d+) (.+?)s?$/, "$1-$2"),
  };
}

export function ExtensionPill(props: ExtensionPillProps) {
  const { originalDueDate, extendedDueDate, alert = false } = props;
  const { article, length } = asExtensionLength(
    formatDistanceStrict(new Date(originalDueDate), new Date(extendedDueDate))
  );
  return (
    <Tooltip
      content={
        <Text kind="bodySm">
          You have {article} <strong>{length}</strong> extension.
        </Text>
      }
    >
      {alert ? (
        <Pill
          color={colors.alertBg}
          textColor={colors.amber800}
          leftSection={<TimeIcon label="" size={16} />}
          lowerCase
          basic
          // `basic` drops the Pill's own role/tabIndex, and the Radix tooltip
          // trigger wraps it `asChild` with no focusable fallback, so `tabIndex`
          // is what keeps the extension tooltip reachable by keyboard. It is not
          // a `role="button"`: the pill runs no action on Enter/Space, so button
          // semantics would misrepresent a plain focusable tooltip trigger to
          // assistive tech.
          tabIndex={0}
        >
          Extension applied
        </Pill>
      ) : (
        <Pill color={colors.white200}>Extension</Pill>
      )}
    </Tooltip>
  );
}
